import { useEffect, useRef, useState } from "react";
import { Box, Chip, IconButton, Paper, TextField, Typography } from "@mui/material";
import { Close, Send } from "@mui/icons-material";
import { helpDocs, type HelpDoc } from "../../datas/HelpDoc";
import { faqDocs } from "../../datas/ChatFaq";

// 챗봇 검색 대상: 매뉴얼 + 챗봇 전용 FAQ
const allDocs = [...helpDocs, ...faqDocs];
type Option = { id: string; title: string };
type Reply = { text: string; options?: Option[] };
type Message = Reply & { role: "user" | "bot" };

const GREETING: Message = {
  role: "bot",
  text: "무엇을 도와드릴까요? 궁금한 메뉴를 선택하거나 직접 입력해 주세요.",
  options: helpDocs.filter((d) => !d.id.includes(" > ")).map((d) => ({ id: d.id, title: d.title })),
};

const norm = (s: string) => s.replace(/\s+/g, "").toLowerCase();
const topIdOf = (id: string) => id.split(" > ")[0];
const toOption = (d: { id: string; title: string }): Option => ({ id: d.id, title: d.title });

export function answerDoc(id: string): Reply {
  const doc = allDocs.find((d) => d.id === id);
  if (!doc) return { text: "해당 항목을 찾지 못했어요." };
  const steps = doc.blocks.flatMap((b) => b.body).slice(0, 5);
  const inManual = helpDocs.includes(doc);
  return {
    text: [`[${doc.id}] ${doc.summary}`, ...steps.map((s) => `• ${s}`), inManual ? "자세한 내용은 매뉴얼에서 확인하세요." : ""]
      .filter(Boolean)
      .join("\n"),
    options: doc.related?.map(toOption),
  };
}

// 후보가 한 상위 메뉴로 모이면 바로 답하고(하위는 버튼), 여러 메뉴로 갈리면 되물음
const resolve = (docs: HelpDoc[]): Reply => {
  const ids = [...new Set(docs.map((d) => topIdOf(d.id)))];
  const tops = ids.map((id) => allDocs.find((d) => d.id === id)).filter((d): d is HelpDoc => !!d);
  if (tops.length === 1) return answerDoc(tops[0].id);
  return { text: "어떤 내용이 궁금하세요?", options: tops.slice(0, 5).map(toOption) };
};

// ponytail: 매뉴얼+FAQ 키워드 매칭. 질문이 다양해지면 이 함수만 백엔드 LLM 호출로 교체
export function answer(question: string): Reply {
  const q = norm(question);

  // 1) 질문에 키워드/제목이 통째로 들어있는 경우
  const scored = allDocs.map((doc) => ({
    doc,
    score: (q.includes(norm(doc.title)) ? 3 : 0) + doc.keywords.filter((k) => q.includes(norm(k))).length * 2,
  }));
  const best = Math.max(...scored.map((s) => s.score));
  if (best > 0) {
    const top = scored.filter((s) => s.score === best).map((s) => s.doc);
    return top.length === 1 ? answerDoc(top[0].id) : resolve(top);
  }

  // 2) "검사"처럼 질문이 키워드의 일부인 경우
  // ponytail: 조사는 끝 한 글자만 떼서 비교("검사는"→"검사"), 형태소 분석은 필요해지면 추가
  const tokens = question
    .split(/\s+/)
    .map(norm)
    .flatMap((t) => (t.length >= 3 ? [t, t.slice(0, -1)] : [t]))
    .filter((t) => t.length >= 2);
  const partial = allDocs.filter((doc) =>
    [doc.title, ...doc.keywords].some((k) => tokens.some((t) => norm(k).includes(t))),
  );
  if (partial.length) return resolve(partial);

  return { text: "관련 내용을 찾지 못했어요. 아래 메뉴에서 골라 주세요.", options: GREETING.options };
}

export default function ChatbotPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!open) return null;

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setMessages((prev) => [...prev, { role: "user", text }, { role: "bot", ...answer(text) }]);
    setInput("");
  };

  const pick = (o: Option) => {
    setMessages((prev) => [...prev, { role: "user", text: o.title }, { role: "bot", ...answerDoc(o.id) }]);
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: 96,
        right: 24,
        zIndex: 1300,
        width: 360,
        height: 520,
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--page-surface-bg)",
        border: "1px solid var(--page-surface-border)",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          color: "#fff",
          background: "linear-gradient(135deg, var(--brand-gradient-from), var(--brand-gradient-to))",
        }}
      >
        <Typography fontWeight={700}>도움말 챗봇</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "inherit" }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {messages.map((m, i) => (
          <Box
            key={i}
            sx={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "85%",
              px: 1.5,
              py: 1,
              borderRadius: 2,
              whiteSpace: "pre-wrap",
              fontSize: 14,
              bgcolor: m.role === "user" ? "var(--brand-gradient-from)" : "var(--header-menu-bg)",
              color: m.role === "user" ? "#fff" : "var(--page-text)",
            }}
          >
            {m.text}
            {m.options && m.options.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                {m.options.map((o) => (
                  <Chip key={o.id} label={o.title} size="small" variant="outlined" color="primary" onClick={() => pick(o)} />
                ))}
              </Box>
            )}
          </Box>
        ))}
        <div ref={bottomRef} />
      </Box>

      <Box sx={{ display: "flex", gap: 1, p: 1, borderTop: "1px solid var(--page-surface-border)" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="궁금한 점을 입력하세요"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) send();
          }}
        />
        <IconButton color="primary" onClick={send}>
          <Send />
        </IconButton>
      </Box>
    </Paper>
  );
}
